import { format } from 'date-fns';
import { type FC } from 'react';
import styled from 'styled-components';

import { useTexts } from '@/hooks/useTexts';
import { fmsWithTemplate } from '@/i18n/fmsWithTemplate';
import { useUserDateFormat } from '@/i18n/hooks';
import { defaultDateFormat } from '@/i18n/hooks/useUserDateFormat';
import { orderExpiryDate } from '@/util/orderExpiryDate';

import { ConnectSurface } from '../Pretzel/ConnectSurface';
import { WithGap } from '../WithGap';
import { GoToSigningButton } from './GoToSigningButton';
import { useOrderToSignData } from './hooks/useOrderToSignData';

export const OrderToSignAlert: FC = () => {
  const texts = useTexts();
  const dateFormat = useUserDateFormat();
  const { data, isSuccess } = useOrderToSignData();

  // Don't display anything while the request is not finished.
  // - The HTTP call should be lightening fast, so don't render anything while fetching.
  // - If there are errors, we don't know what failed, so don't display anything.
  if (!isSuccess || data?.pendingOrders.length === 0) {
    return null;
  }

  return (
    <WithGapWithTopMargin data-test="pending-orders-alerts">
      {data!.pendingOrders.map((order) => (
        <StyledSurface key={order.orderId}>
          <WithGap gap="16" data-test={`order-to-sign-${order.orderId}`}>
            <Heading>
              <i data-phosphor-icons="true" style={{ fontSize: '32px' }}>
                information_filled
              </i>
              <h1>{texts.pendingOrders.title}</h1>
            </Heading>

            <div className="font-body">
              <p>
                {texts.pendingOrders.description.part1}{' '}
                <span className="font-body-demibold">{`${
                  data!.portfolios.get(order.portfolioNumber)!.name
                } (${order.portfolioNumber})`}</span>{' '}
                {texts.pendingOrders.description.part2}{' '}
              </p>

              <p>
                {texts.pendingOrders.description.part3}{' '}
                <span>{format(order.createdDate, dateFormat)}</span>{' '}
                {texts.pendingOrders.description.part4}{' '}
                <span>{order.createdBy}</span>
              </p>
            </div>

            <p className="font-body-demibold" data-test="expiry-date">
              {fmsWithTemplate(texts.pendingOrders.description.part5, {
                date: format(
                  orderExpiryDate(order.createdDate),
                  `${defaultDateFormat} HH:mm`,
                ),
              })}
            </p>

            <GoToSigningButton pendingOrder={order} />
          </WithGap>
        </StyledSurface>
      ))}
    </WithGapWithTopMargin>
  );
};

const WithGapWithTopMargin = styled(WithGap)`
  margin-top: 16px;
`;

const StyledSurface = styled(ConnectSurface)`
  border: 3px solid var(--p-color-stroke-0);
`;

const Heading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  h1 {
    margin: 0;
    font-size: 24px;
  }
`;
